import { type ImageField } from 'components/DropzoneField'
import { fetchImage } from 'pagesNew/SuccessStory/Edit/hooks'
import { uploadImage } from 'utils/saveImageS3'

// tagged via name instead of subclassing Error, which loses instanceof support on the es5 target
const IMAGE_UPLOAD_ERROR_NAME = 'ImageUploadError'

export const isImageUploadError = (error: unknown): error is Error =>
  error instanceof Error && error.name === IMAGE_UPLOAD_ERROR_NAME

export const loadImageFile = async (imagePath: string | null | undefined): Promise<ImageField | undefined> => {
  if (!imagePath) {
    return undefined
  }

  const imageFile = await fetchImage(imagePath)

  if (imageFile) {
    return {
      file: imageFile,
      src: imagePath,
      isUploaded: true,
      initialValue: imagePath,
    }
  }

  return undefined
}

export const uploadImageFile = async (
  image: ImageField | undefined,
  pathS3: string,
  isFullPath?: boolean
): Promise<string | undefined> => {
  const imageFile = !image?.isUploaded ? image?.file : null

  if (imageFile) {
    const uploadedPath = await uploadImage(imageFile, pathS3, isFullPath)

    if (!uploadedPath) {
      const error = new Error('Failed to upload image')
      error.name = IMAGE_UPLOAD_ERROR_NAME

      throw error
    }

    return uploadedPath
  }

  return image?.initialValue
}

// params can be in json like '["path-to/image-1","path-to/image-2","path-to/image-3"]' or just 'path-to/image-1'
const parseImagePaths = (imagePaths: string | null | undefined): string[] | undefined => {
  if (!imagePaths) {
    return undefined
  }

  try {
    const parsed = JSON.parse(imagePaths)
    if (Array.isArray(parsed)) {
      if (!parsed.length) {
        return undefined
      }
      return parsed
    }
    return [imagePaths]
  } catch (e) {
    return [imagePaths]
  }
}

// result can be in json like '["path-to/image-1","path-to/image-2","path-to/image-3"]' or just 'path-to/image-1'
const stringifyImagePaths = (imagePaths: string[] | null | undefined): string | undefined => {
  if (!imagePaths || !imagePaths.length) {
    return undefined
  }

  if (imagePaths.length > 1) {
    return JSON.stringify(imagePaths)
  }
  return imagePaths[0]
}

// receives images paths in json like '["path-to/image-1","path-to/image-2","path-to/image-3"]' or just 'path-to/image-1'
// returns array of ImageField - file for each image
export const loadImageFiles = async (paths: string | null | undefined): Promise<ImageField[] | undefined> => {
  const imagePaths = parseImagePaths(paths)

  if (!imagePaths) {
    return undefined
  }

  const imageFiles = await Promise.all(imagePaths.map((imagePath) => loadImageFile(imagePath)))
  const files = imageFiles.filter(Boolean)

  if (files.length) {
    return files as ImageField[]
  }

  return undefined
}

// receives array of ImageField - file for each image
// returns paths in json like '["path-to/image-1","path-to/image-2","path-to/image-3"]' or just 'path-to/image-1'
export const uploadImageFiles = async (
  images: ImageField[] | undefined,
  pathS3: string,
  isFullPath?: boolean
): Promise<string | undefined> => {
  if (!images || !images.length) {
    return undefined
  }

  const imagePaths = await Promise.all(images.map((image) => uploadImageFile(image, pathS3, isFullPath)))
  const paths = imagePaths.filter(Boolean) as string[]

  return stringifyImagePaths(paths)
}
